import { useQuery } from '@tanstack/react-query'
import { Button, Card, DatePicker, Flex } from 'antd'
import axios from 'axios'
import dayjs from 'dayjs'
import queryString from 'query-string'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { useQuestionnairesServiceGetQuestionnaireStatsEndpoint } from 'src/api/queries'
import { GetQuestionnaireResponse } from 'src/api/requests'
import { ExportDocumentIcon } from 'src/assets'
import { InternalInstitutionsSelect, Text } from 'src/components'
import { dayMonthYearBackSlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { parseToken } from 'src/utils'

import { useStyles } from '../styles'
import { mapAnswersToQuiz } from '../utils'

import { Choices } from './Choices'

type Props = {
  data?: GetQuestionnaireResponse
}

export const QuestionnairesStats = ({ data }: Props) => {
  const { id } = useParams()
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()

  const [date, setDate] = useState<string[] | undefined>(undefined)

  const [institution, setInstitution] = useState()

  const queryParams = {
    id: String(id),
    filterId: institution,
    ...(date?.[0] &&
      date?.[1] && {
        fromDate: date?.[0],
        toDate: date?.[1]
      })
  }

  const { data: selectedAnswers, isLoading } =
    useQuestionnairesServiceGetQuestionnaireStatsEndpoint(queryParams)

  const mappedQuiz = mapAnswersToQuiz(data, selectedAnswers)

  const queryStringified = queryString.stringify(queryParams, {
    arrayFormat: 'none',
    skipNull: true,
    skipEmptyString: true
  })

  const exportInventoryList = async () => {
    const response = await axios.get(
      `${import.meta.env.VITE_BASE_URL}/api/questionnaires/${id}/stats/export?${queryStringified}`,
      {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${parseToken(localStorage.getItem('access_token'))}`,
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        }
      }
    )

    return response
  }

  const useExportInventoryList = (exportQueryParams: string) => {
    return useQuery({
      queryKey: ['exportInventory', exportQueryParams],
      queryFn: () => exportInventoryList(),
      enabled: false
    })
  }

  const { refetch, isLoading: isExportLoading } =
    useExportInventoryList(queryStringified)

  const onExport = async () => {
    try {
      const response = await refetch()

      if (!response?.data) {
        return
      }

      const blob = new Blob([response.data?.data], {
        type: response.data.data?.type
      })
      const url = window.URL.createObjectURL(blob)

      const a = document.createElement('a')
      a.href = url
      a.download = `${data?.name?.[currentLang?.value]}.xlsx`
      document.body.appendChild(a)
      a.click()

      window.URL.revokeObjectURL(url)
    } catch {
      //empty
    }
  }

  return (
    <Flex vertical gap={24}>
      <Flex gap={24}>
        <Card loading={isLoading} className="full-width">
          <Flex gap={8}>
            {(selectedAnswers?.count ?? 0) > 0 ? (
              <>
                <Text weight={700}>
                  {t('questionnaires.persons_count_completed_questionnaire')} :
                </Text>

                <Text>{selectedAnswers?.count}</Text>
              </>
            ) : (
              t('questionnaires.no_person_completed_this_questionnaire')
            )}
          </Flex>
        </Card>
      </Flex>

      <Card loading={isLoading}>
        <Flex gap={8}>
          <InternalInstitutionsSelect
            value={institution}
            disabled={false}
            onChange={setInstitution}
          />

          <DatePicker.RangePicker
            allowClear
            allowEmpty
            disabled={false}
            value={
              date?.[0] && date?.[1]
                ? [
                    dayjs(date[0], dayMonthYearBackSlash, true),
                    dayjs(date[1], dayMonthYearBackSlash, true)
                  ]
                : undefined
            }
            format={dayMonthYearBackSlash}
            className="full-width"
            placeholder={[
              t('notifications.from_date'),
              t('notifications.to_date')
            ]}
            onChange={(_, val) => setDate(val)}
          />

          <Button
            disabled={!selectedAnswers}
            icon={<ExportDocumentIcon />}
            loading={isExportLoading}
            onClick={onExport}
          >
            {t('common.export')}
          </Button>
        </Flex>
      </Card>

      <Card className={styles.statsCard} loading={isLoading}>
        <Flex vertical gap={48}>
          <Text className={styles.quizTitle} weight={700}>
            {mappedQuiz?.name?.[currentLang?.value]}
          </Text>
          {mappedQuiz?.questions?.map(
            (question: GetQuestionnaireResponse, index: number) => (
              <Flex vertical gap={16} key={question?.id}>
                <Text className={styles.questionTitle}>
                  {index + 1}. {question?.text?.[currentLang?.value]}
                </Text>

                <Choices
                  choices={question.answerChoices}
                  totalResponses={question.totalResponses}
                />
              </Flex>
            )
          )}
        </Flex>
      </Card>
    </Flex>
  )
}
